import { KubernetesSetupFormData } from '@/react/portainer/policies/components/kubernetes/setup/types';

import { FormSection } from '@@/form-components/FormSection';
import { Badge } from '@@/Badge';

import { PolicyTableWrapper } from './PolicyTableWrapper';

function SettingsList({ children }: { children: React.ReactNode }) {
  return (
    <div className="space-y-1 overflow-hidden rounded-lg border border-gray-5 th-dark:border-gray-7">
      {children}
    </div>
  );
}

function SettingItem({
  label,
  value,
}: {
  label: string;
  value: boolean | undefined;
}) {
  return (
    <div className="flex items-center justify-between bg-gray-2 px-4 py-3 th-dark:bg-gray-iron-10">
      <span className="text-sm">{label}</span>
      <Badge type={value ? 'success' : 'muted'}>
        {value ? 'Enabled' : 'Disabled'}
      </Badge>
    </div>
  );
}

function IndentedValueRow({
  label,
  value,
  isLast,
}: {
  label: string;
  value: string;
  isLast?: boolean;
}) {
  return (
    <div
      className={`ml-4 flex items-center justify-between bg-gray-2 px-4 py-3 th-dark:bg-gray-iron-10 ${
        isLast ? 'rounded-b-lg' : ''
      }`}
    >
      <span className="text-sm">{label}</span>
      <span className="text-sm">{value}</span>
    </div>
  );
}
interface Props {
  formData: KubernetesSetupFormData;
}
/**
 * Renders the read-only setup policy sections in a fixed layout for scanning.
 */
export function SetupPolicyReadOnly({ formData }: Props) {
  return (
    <div className="space-y-6">
      <FormSection title="Networking">
        <div className="space-y-4">
          <SectionSubtitle>Services</SectionSubtitle>
          <SettingsList>
            <SettingItem
              label="Use LoadBalancer service type"
              value={formData.useLoadBalancer}
            />
          </SettingsList>

          <SectionSubtitle>Ingresses</SectionSubtitle>
          <SettingsList>
            <SettingItem
              label="Allow using no ingress class"
              value={formData.allowNoneIngressClass}
            />
            <SettingItem
              label="Ingress availability per namespace"
              value={formData.ingressAvailabilityPerNamespace}
            />
            <SettingItem
              label="Restrict standard user ingress writes"
              value={formData.restrictStandardUserIngressW}
            />
          </SettingsList>

          {formData.ingressControllers?.length > 0 && (
            <>
              <SectionSubtitle>Ingress Controllers</SectionSubtitle>
              <PolicyTableWrapper caption="Configured ingress controllers for this policy">
                <thead className="bg-gray-3 th-dark:bg-gray-9">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-medium">
                      Name
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
                  {formData.ingressControllers.map((ic) => (
                    <tr key={ic.name}>
                      <td className="px-4 py-3 text-sm">{ic.name}</td>
                    </tr>
                  ))}
                </tbody>
              </PolicyTableWrapper>
            </>
          )}
        </div>
      </FormSection>

      <FormSection title="Change Window">
        <SettingsList>
          <SettingItem
            label="GitOps change window"
            value={formData.changeWindow?.Enabled}
          />
          {formData.changeWindow?.Enabled && (
            <IndentedValueRow
              label="Window"
              value={formatChangeWindow(
                formData.changeWindow.StartTime,
                formData.changeWindow.EndTime
              )}
              isLast
            />
          )}
        </SettingsList>
      </FormSection>

      <FormSection title="Resources and Metrics">
        <SettingsList>
          <SettingItem
            label="Enable resource over-commit"
            value={formData.enableResourceOverCommit}
          />
          {formData.enableResourceOverCommit && (
            <IndentedValueRow
              label="Over-commit percentage"
              value={`${formData.resourceOverCommitPercentage}%`}
            />
          )}
          <SettingItem
            label="Use server metrics"
            value={formData.useServerMetrics}
          />
        </SettingsList>
      </FormSection>

      {formData.storageClasses?.length > 0 && (
        <FormSection title="Storage">
          <PolicyTableWrapper caption="Storage class configuration including access modes and volume expansion settings">
            <thead className="bg-gray-3 th-dark:bg-gray-9">
              <tr>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Storage Class
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Access Modes
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Volume Expansion
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-5 th-dark:divide-gray-7">
              {formData.storageClasses.map((sc) => (
                <tr key={sc.name}>
                  <td className="px-4 py-3 text-sm font-medium">{sc.name}</td>
                  <td className="px-4 py-3 text-sm">
                    {sc.accessModes?.join(', ') || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge value={sc.allowVolumeExpansion} />
                  </td>
                </tr>
              ))}
            </tbody>
          </PolicyTableWrapper>
        </FormSection>
      )}
    </div>
  );
}

/**
 * Labels sub-sections so related settings are easier to scan.
 */
function SectionSubtitle({ children }: { children: React.ReactNode }) {
  return (
    <h5 className="text-sm font-semibold tracking-wide text-gray-6 uppercase th-dark:text-gray-warm-5">
      {children}
    </h5>
  );
}

/**
 * Converts boolean values into a stable enabled/disabled badge display.
 */
function StatusBadge({ value }: { value: boolean | undefined }) {
  return (
    <Badge type={value ? 'success' : 'muted'}>
      {value ? 'Enabled' : 'Disabled'}
    </Badge>
  );
}

/**
 * Normalizes the change window fields into a single human-readable string.
 */
function formatChangeWindow(start?: string, end?: string): string {
  if (start && end) {
    return `${start} - ${end}`;
  }

  if (start) {
    return start;
  }

  if (end) {
    return end;
  }

  return 'Not configured';
}
